import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { Department } from '@/types/departments';

interface DepartmentMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  departmentRole: string;
}

interface DepartmentState {
  departments: Department[];
  isLoading: boolean;
  error: string | null;
  members: DepartmentMember[];
  fetchDepartments: () => Promise<void>;
  createDepartment: (data: Partial<Department>) => Promise<Department>;
  updateDepartment: (id: string, data: Partial<Department>) => Promise<void>;
  deleteDepartment: (id: string) => Promise<void>;
  fetchMembers: (departmentId: string) => Promise<void>;
}

export const useDepartmentStore = create<DepartmentState>((set, get) => ({
  departments: [],
  isLoading: false,
  error: null,
  members: [],

  fetchDepartments: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('departments')
        .select(`
          *,
          manager:manager_id (
            id,
            full_name,
            email,
            role
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ departments: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch departments';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  createDepartment: async (data) => {
    try {
      set({ isLoading: true, error: null });
      const { data: newDepartment, error } = await supabase
        .from('departments')
        .insert([data])
        .select(`
          *,
          manager:manager_id (
            id,
            full_name,
            email,
            role
          )
        `)
        .single();

      if (error) throw error;
      
      // Update local state
      set(state => ({
        departments: [newDepartment, ...state.departments]
      }));

      return newDepartment;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create department';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  updateDepartment: async (id, data) => {
    try {
      set({ isLoading: true, error: null });
      
      const { error } = await supabase
        .from('departments')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Refresh departments list
      await get().fetchDepartments();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update department';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  deleteDepartment: async (id) => {
    try {
      set({ isLoading: true, error: null });

      // Start a transaction using RPC
      const { error: rpcError } = await supabase.rpc('delete_department_cascade', {
        p_department_id: id
      });

      if (rpcError) throw rpcError;

      // Update local state by removing the deleted department
      set(state => ({
        departments: state.departments.filter(dept => dept.id !== id)
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete department';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchMembers: async (departmentId: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .eq('department_id', departmentId)
        .eq('role', 'worker')
        .eq('status', 'active');

      if (error) throw error;

      const members = users.map(user => ({
        id: user.id,
        firstName: user.full_name?.split(' ')[0] || '',
        lastName: user.full_name?.split(' ').slice(1).join(' ') || '',
        email: user.email,
        role: user.role,
        departmentRole: 'member'
      }));

      set({ members });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch members';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },
}));

// Set up realtime subscription
supabase
  .channel('departments_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'departments' },
    () => {
      useDepartmentStore.getState().fetchDepartments();
    }
  )
  .subscribe();